import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
// Import directly to avoid circular dependency (HoursRecordsView cannot be in registry since it imports from it)
import HoursChart from './HoursChart';
import HoursExportDropdown from './HoursExportDropdown';
// FT-196: Standardise to use shared SignatureModal instead of HoursSignatureModal
import SignatureModal from '../../../shared/SignatureModal';
import HoursDigitalSignatures from './HoursDigitalSignatures';
import HoursRecordsTable from './HoursRecordsTable';
import ConfirmationModal from '../../common/ConfirmationModal';
import { exportHoursToWindow, exportMultiMonthChart } from '../../../../utils/hoursExportSimple';
import { toLocalYMD } from '../../../../utils/dateFmt';
// FT-32b: Import for updating crew member's horVessel field
import { useFleetCrew } from '../../../../hooks/domain/fleet/useFleetCrew';

// Parse "HH:MM" -> minutes since midnight
function parseHHMM(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]), mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
}

// Complement of rest = work (inside 0..1440)
function complementAsWork(restIntervals) {
  const work = [];
  let t = 0;
  for (const [rs,re] of restIntervals) {
    if (t < rs) work.push([t, rs]);
    t = Math.max(t, re);
  }
  if (t < 1440) work.push([t, 1440]);
  return work;
}

function minutesOverlap(a0,a1,b0,b1) {
  const x = Math.min(a1, b1) - Math.max(a0, b0);
  return x > 0 ? x : 0;
}

const HoursRecordsView = ({
  selectedMonth,
  setSelectedMonth,
  selectedCrew,
  selectedCrewId,
  selectedDate,
  crew = [],
  hoursData = {}
}) => {
  // crewKey: tolerate id string or object {id:...}
  const crewKey =
    (typeof selectedCrewId === 'string' && selectedCrewId) ||
    (typeof selectedCrew === 'string' && selectedCrew) ||
    (selectedCrew && typeof selectedCrew === 'object' && selectedCrew.id) ||
    null;
  
  // Show all non-archived crew (includes both On Duty and Off Duty)
  const activeCrew = crew.filter(c => c.status !== 'archived');

  // FT-32b: Get update function for crew member horVessel field
  const { update: updateCrewMember } = useFleetCrew();

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureType, setSignatureType] = useState('crew');
  const [signatures, setSignatures] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Force refresh when data changes
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Watch for hoursData changes and force refresh
  React.useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [hoursData, selectedCrew, selectedMonth]);

  // Get days in month
  const daysInMonth = useMemo(() => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const lastDay = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 1; i <= lastDay; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  }, [selectedMonth]);

  // Normalize hoursData into a flat array of records
  const flatHours = React.useMemo(() => {
    if (Array.isArray(hoursData)) return hoursData;
    if (hoursData && typeof hoursData === 'object') {
      // common shapes: {data:[...]}, or { [crewId]: { [date]: record } }
      if (Array.isArray(hoursData.data)) return hoursData.data;
      const out = [];
      for (const dates of Object.values(hoursData)) {
        if (dates && typeof dates === 'object') {
          for (const rec of Object.values(dates)) if (rec) out.push(rec);
        }
      }
      return out;
    }
    return [];
  }, [hoursData]);

  // Build index from FLAT ARRAY → { [crewId]: { [YYYY-MM-DD]: record } }
  const hoursByCrewDate = React.useMemo(() => {
    const m = {};
    flatHours.forEach(r => {
      if (!r?.crewId || !r?.date) return;
      (m[r.crewId] ||= {})[r.date] = r;
    });
    return m;
  }, [flatHours]);


  // Navigate months
  const previousMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setSelectedMonth(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setSelectedMonth(newMonth);
  };

  // Get hours for a specific day - reads 'periods' or 'workPeriods' array
  // Returns { cells, workHours, restHours, isCompliant }
  // NOTE: This takes a RECORD object, not (crewId, date). For export, use getDayHoursForExport.
  // FT-156: Now supports workPeriods (work times) in addition to periods (rest times)
  const getDayHours = (record) => {
    if (!record) {
      // No data for this day = full rest day (24h rest)
      const cells = Array(24).fill(null).map(() => ({ type: 'rest', partial: false, ratio: 1 }));
      return { cells, workHours: 0, restHours: 24, isCompliant: true };
    }

    // FT-156: Check for workPeriods first (new format), then periods (legacy REST format)
    const workPeriodsRaw = Array.isArray(record?.workPeriods) ? record.workPeriods : [];
    const legacyPeriods = Array.isArray(record?.periods) ? record.periods : [];

    // Expand wrap-around periods (22:00→06:00) into same-day segments
    const expanded = [];

    // FT-156: If workPeriods exist, use them to calculate REST intervals
    if (workPeriodsRaw.length > 0) {
      // Expand work periods into minute intervals
      const workExpanded = [];
      for (const p of workPeriodsRaw) {
        const s = parseHHMM(p.start);
        const e = parseHHMM(p.end);
        if (s == null || e == null) continue;

        if (e > s) {
          workExpanded.push([s, e]);
        } else if (e < s) {
          // Wrap around midnight
          workExpanded.push([s, 1440]);
          workExpanded.push([0, e]);
        }
      }

      // Sort work intervals by start time and merge overlaps
      workExpanded.sort((a, b) => a[0] - b[0]);
      const merged = [];
      for (const [ws, we] of workExpanded) {
        if (merged.length === 0 || merged[merged.length - 1][1] < ws) {
          merged.push([ws, we]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], we);
        }
      }

      // REST = complement of WORK
      // complementAsWork gives us non-rest intervals, so we need to invert logic
      // expanded should contain REST intervals (when not working)
      let t = 0;
      for (const [ws, we] of merged) {
        if (t < ws) expanded.push([t, ws]); // REST before this work period
        t = Math.max(t, we);
      }
      if (t < 1440) expanded.push([t, 1440]); // REST after last work period
    } else {
      // Legacy: read periods as REST intervals directly
      for (const p of legacyPeriods) {
        const s = parseHHMM(p.start);
        const e = parseHHMM(p.end);
        if (s == null || e == null) continue;

        if (e > s) {
          // Normal same-day period
          expanded.push([s, e]);
        } else if (e < s) {
          // Wrap around midnight: [s, 24:00) + [0, e]
          expanded.push([s, 1440]); // 1440 = 24:00
          expanded.push([0, e]);
        }
        // If e === s, skip (0-duration period)
      }
    }

    // If no periods but we have legacy fields, convert them
    if (expanded.length === 0 && (record?.workStart || record?.workEnd)) {
      const s = parseHHMM(record.workStart);
      const e = parseHHMM(record.workEnd);
      if (s != null && e != null && s !== e) {
        // This is WORK time, so REST is the complement
        const workIntervals = s < e ? [[s, e]] : [[s, 1440], [0, e]];
        // Get REST by inverting work intervals
        const rest = complementAsWork(workIntervals);
        expanded.push(...rest);
      }
    }

    // FT-156b FALLBACK: If still no periods but we have workHours, generate approximate periods
    // Assume standard day shift starting at 09:00 (e.g., 8h work → 09:00-17:00)
    if (expanded.length === 0 && typeof record?.workHours === 'number' && record.workHours > 0) {
      const workMins = Math.round(record.workHours * 60);
      const startMins = 9 * 60; // 09:00
      const endMins = Math.min(startMins + workMins, 1440); // Cap at midnight

      // Calculate REST intervals (complement of work)
      // REST is before work (00:00-09:00) and after work (17:00-24:00 for 8h shift)
      if (startMins > 0) {
        expanded.push([0, startMins]); // Early morning rest
      }
      if (endMins < 1440) {
        expanded.push([endMins, 1440]); // Evening rest
      }
    }
    
    // Build the 24-hour grid
    let restTotalMins = 0;
    const cells = [];
    
    for (let hour = 0; hour < 24; hour++) {
      const hStart = hour * 60;
      const hEnd = hStart + 60;
      let restMins = 0;
      
      // Calculate REST minutes in this hour
      for (const [rs, re] of expanded) {
        restMins += minutesOverlap(hStart, hEnd, rs, re);
      }
      
      // Clamp to 0-60 range
      restMins = Math.max(0, Math.min(60, restMins));
      restTotalMins += restMins;
      
      const ratio = restMins / 60; // 0..1
      // Tolerance thresholds: >= 59 min = full rest, <= 1 min = full work
      // This fixes edge cases like hour 23 showing gradient when rest starts at 23:00
      const isFullRest = restMins >= 59;
      const isFullWork = restMins <= 1;
      const partial = !isFullRest && !isFullWork; // Only true for genuine partial hours (2-58 minutes)
      
      // Type based on dominant activity
      const type = restMins >= 30 ? 'rest' : 'work';
      
      cells.push({ type, partial, ratio });
    }
    
    // Round to nearest 15 minutes to avoid floating point weirdness (8.02 hours)
    const restHours = Math.round(restTotalMins / 15) * 0.25;
    const workHours = 24 - restHours;
    
    // Check compliance (minimum 10 hours rest)
    const isCompliant = restHours >= 10;
    
    return { cells, workHours, restHours, isCompliant };
  }; // Plain function - no useCallback to avoid stale closures

  // FT-32: Adapter function for export utilities
  // Takes (crewId, date) and returns shape expected by hoursExportSimple.js:
  // { hours: ['W'|'R'|'-'...], totalWork, totalRest, isCompliant }
  const getDayHoursForExport = (crewId, date) => {
    // Build date string in YYYY-MM-DD format
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;

    // Look up the record
    const record = hoursByCrewDate[crewId]?.[dateStr];

    // Get parsed day data using existing function
    const dayData = getDayHours(record);

    // Convert cells array to hours array ('W' or 'R' for each hour)
    const hours = dayData.cells.map(cell => {
      if (cell.type === 'work') return 'W';
      if (cell.type === 'rest') return 'R';
      return '-';
    });

    // Return in the shape the export expects
    return {
      hours,
      totalWork: dayData.workHours,
      totalRest: dayData.restHours,
      isCompliant: dayData.isCompliant
    };
  };

  // Calculate monthly summary - memoized for performance
  const monthlySummary = useMemo(() => {
    if (!crewKey) return { totalWork: 0, totalRest: 0, violations: 0 };
    
    let totalWork = 0;
    let totalRest = 0;
    let violations = 0;
    
    daysInMonth.forEach(date => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;     // LOCAL date, no UTC shift
      const record = hoursByCrewDate[crewKey]?.[dateStr];
      const dayData = record ? getDayHours(record) : { workHours: 0, restHours: 24, isCompliant: true };
      
      if (dayData.workHours > 0 || dayData.restHours > 0) {
        totalWork += dayData.workHours || 0;
        totalRest += dayData.restHours || 0;
        if (!dayData.isCompliant) violations++;
      } else {
        // No data = full rest day
        totalRest += 24;
      }
    });
    
    return { totalWork, totalRest, violations };
  }, [crewKey, daysInMonth, hoursByCrewDate]); // Recalculate when crew, month, or data changes

  // Export single month to new window
  // FT-32: Use getDayHoursForExport adapter (takes crewId, date → returns {hours, totalWork, totalRest, isCompliant})
  const handleExport = () => {
    if (!crewKey || !currentCrewMember) return;

    exportHoursToWindow(
      crewKey,
      currentCrewMember,
      selectedMonth,
      daysInMonth,
      getDayHoursForExport,
      summary,
      signatures
    );
  };

  // Export multi-month summary
  // FT-32: Use getDayHoursForExport adapter for consistent data shape
  const handleMultiMonthExport = (monthCount = 3) => {
    if (!crewKey || !currentCrewMember) return;

    // Start from 2 months back to include current month
    const startMonth = new Date(selectedMonth);
    startMonth.setMonth(startMonth.getMonth() - (monthCount - 1));

    exportMultiMonthChart(
      crewKey,
      currentCrewMember,
      startMonth,
      monthCount,
      getDayHoursForExport
    );
  };

  const summary = monthlySummary;
  const currentCrewMember = activeCrew.find(c => c.id === crewKey);

  // FT-32b: Handler for updating crew member's horVessel field
  // Plain function - no useCallback to avoid stale closures (consistent with file pattern)
  const handleHorVesselChange = (vessel) => {
    if (!crewKey) return;
    updateCrewMember(crewKey, { horVessel: vessel || null });
  };

  // Load saved signatures from localStorage
  React.useEffect(() => {
    if (crewKey && selectedMonth) {
      const monthKey = toLocalYMD(selectedMonth).slice(0, 7); // YYYY-MM
      const savedSignatures = localStorage.getItem(`hor_signatures_${crewKey}_${monthKey}`);
      if (savedSignatures) {
        setSignatures(JSON.parse(savedSignatures));
      } else {
        setSignatures({});
      }
    }
  }, [crewKey, selectedMonth]);

  // Handle signature save
  // FT-196: Updated handler to work with shared SignatureModal API
  // SignatureModal.onSave signature: (sigObj, signatureType) where sigObj = { data, name, position, timestamp }
  const handleSignatureSave = (sigObj, type) => {
    const monthKey = toLocalYMD(selectedMonth).slice(0, 7);

    // Map shared SignatureModal types back to HOR types
    // 'individual' -> 'crew', 'authorized' -> 'master'
    const horType = type === 'individual' ? 'crew' : 'master';

    let signedBy;
    let role = null;

    if (horType === 'crew') {
      signedBy = currentCrewMember?.full_name || currentCrewMember?.name;
    } else {
      // For authorized person, use the provided name from the modal
      signedBy = sigObj.name || 'Authorized Person';
      role = sigObj.position || null;
    }

    const signatureInfo = {
      data: sigObj.data,
      timestamp: toLocalYMD(new Date()),
      signedBy: signedBy
    };

    // Add role if provided
    if (role) {
      signatureInfo.role = role;
    }

    const newSignatures = {
      ...signatures,
      [horType]: signatureInfo
    };

    setSignatures(newSignatures);
    localStorage.setItem(`hor_signatures_${crewKey}_${monthKey}`, JSON.stringify(newSignatures));
    setShowSignatureModal(false);
  };

  // Open signature modal
  const openSignatureModal = (type) => {
    setSignatureType(type);
    setShowSignatureModal(true);
  };

  // Clear signature
  const handleClearSignature = (type) => {
    setConfirmAction({ type: 'clear', signatureType: type });
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirm = () => {
    if (confirmAction?.type === 'clear') {
      const monthKey = toLocalYMD(selectedMonth).slice(0, 7);
      const newSignatures = { ...signatures };
      delete newSignatures[confirmAction.signatureType];
      
      setSignatures(newSignatures);
      localStorage.setItem(`hor_signatures_${crewKey}_${monthKey}`, JSON.stringify(newSignatures));
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
  };

  return (
    <div className="bg-white hours-records-view">
      {/* Header */}
      <div className="p-6 no-print">
        <div className="flex items-center justify-end mb-4">
          <HoursExportDropdown
            onExportSingleMonth={handleExport}
            onExportMultiMonth={handleMultiMonthExport}
            selectedMonth={selectedMonth}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* FT-32b: Vessel (optional) - for HOR PDF */}
          {currentCrewMember && (
            <div>
              <label htmlFor="hor-vessel" className="block text-sm font-medium text-gray-700 mb-1">
                Vessel (optional)
              </label>
              <input
                type="text"
                id="hor-vessel"
                value={currentCrewMember.horVessel || ''}
                onChange={(e) => handleHorVesselChange(e.target.value)}
                placeholder="Leave blank if working across multiple vessels"
                className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm py-2 px-3"
              />
            </div>
          )}

          {/* Month Navigation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Month
            </label>
            <div className="flex items-center gap-2">
              <button 
                onClick={previousMonth}
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <div className="flex-1 text-center font-medium">
                {selectedMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
              </div>
              <button 
                onClick={nextMonth}
                className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded"
                disabled={selectedMonth.getMonth() === new Date().getMonth() && selectedMonth.getFullYear() === new Date().getFullYear()}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {crewKey && (
        <div className="px-6 pb-6 no-print">
          <HoursChart 
            key={`${crewKey}-${toLocalYMD(selectedMonth)}-${JSON.stringify(hoursData[crewKey])}`}
            selectedMonth={selectedMonth} 
            selectedCrew={crewKey} 
            hoursData={hoursData} 
          />
        </div>
      )}

      {/* Records Table */}
      {crewKey ? (
        <>
          <HoursRecordsTable 
            key={refreshKey}
            daysInMonth={daysInMonth}
            selectedCrew={crewKey}
            hoursByCrewDate={hoursByCrewDate}
            getDayHours={getDayHours}
            summary={summary}
            currentCrewMember={currentCrewMember}
            selectedMonth={selectedMonth}
          />
          
          <div className="px-6 pb-6">
            {/* Digital Signature Area */}
            <HoursDigitalSignatures 
              signatures={signatures}
              currentCrewMember={currentCrewMember}
              onSignatureClick={openSignatureModal}
              onClearSignature={handleClearSignature}
            />
            
            {/* Print Signature Area */}
            <div className="mt-6 hidden print:block">
              <div className="border-t border-gray-400 pt-4">
                <p className="text-sm">Crew Member Signature: _______________________</p>
                <p className="text-sm mt-2">Authorized Person Signature: _______________________</p>
                <p className="text-sm mt-2">Date: _______________________</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="border-t border-gray-200 p-12 text-center">
          <div className="mx-auto max-w-sm">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No crew member selected</h3>
            <p className="mt-1 text-sm text-gray-500">Select a crew member above to view their hours of rest records.</p>
          </div>
        </div>
      )}

      {/* FT-196: Standardised to use shared SignatureModal */}
      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => setShowSignatureModal(false)}
        onSave={handleSignatureSave}
        signatureType={signatureType === 'crew' ? 'individual' : 'authorized'}
        title={signatureType === 'crew' ? 'Crew Member Signature' : 'Authorized Person Signature'}
        description="Please sign below to confirm the Hours of Rest records for this month."
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConfirmAction(null);
        }}
        onConfirm={handleConfirm}
        title="Clear Signature"
        message={`Are you sure you want to clear the ${confirmAction?.signatureType === 'crew' ? 'crew member' : 'authorized person'} signature?`}
        confirmText="Clear"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

export default HoursRecordsView;